import React from "react";
import { motion } from "framer-motion";
import FinansXIcon from "../assets/finansx.png";
import PetVitalsIcon from "../assets/petvitals.png";
import SectionTitle from "./SectionTitle";
import { mobileApps } from "../content/home";

const iconMap = { finansx: FinansXIcon, petvitals: PetVitalsIcon };

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

const MobileApps = () => {
  return (
    <section id="uygulamalar" className="relative scroll-mt-24 py-24">
      <div className="mx-auto max-w-7xl container-px">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="section-eyebrow mb-4">
          <span className="h-px w-8 bg-brand-500" /> {mobileApps.eyebrow}
        </motion.div>
        <motion.div variants={fadeUp} custom={1} initial="hidden" whileInView="show" viewport={{ once: true }}>
          <SectionTitle lead={mobileApps.title.lead} accent={mobileApps.title.accent} tail={mobileApps.title.tail} className="max-w-2xl" />
        </motion.div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2">
          {mobileApps.apps.map((a, i) => (
            <motion.div
              key={a.name}
              variants={fadeUp}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              className="glass-card group p-7 transition-colors hover:border-black/20 dark:hover:border-white/20"
            >
              <div className="flex items-start gap-5">
                <div className="grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-2xl border border-black/10 bg-black/[0.04] dark:border-white/10 dark:bg-white/[0.04]">
                  <img src={iconMap[a.icon]} alt={a.name} className="h-full w-full object-cover" />
                </div>
                <div>
                  <h3 className="font-display text-xl font-semibold text-gray-900 dark:text-white">{a.name}</h3>
                  <p className="text-sm text-gray-600 dark:text-white/55">{a.tagline}</p>
                </div>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-gray-700 dark:text-white/70">{a.desc}</p>
              <div className="mt-5 flex flex-wrap gap-1.5">
                {a.stack.map((s) => (
                  <span key={s} className="chip">{s}</span>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                <a
                  href={a.appStore}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-gray-900 px-4 py-2 text-sm font-medium text-white transition-transform hover:-translate-y-0.5 dark:bg-white dark:text-ink-900"
                >
                  <svg viewBox="0 0 384 512" className="h-4 w-4" fill="currentColor">
                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
                  </svg>
                  {mobileApps.appStoreLabel}
                </a>
                {a.playStore ? (
                  <a
                    href={a.playStore}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-sm font-medium text-white transition-transform hover:-translate-y-0.5"
                  >
                    <svg viewBox="0 0 512 512" className="h-4 w-4" fill="currentColor">
                      <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
                    </svg>
                    {mobileApps.googlePlayLabel}
                  </a>
                ) : (
                  <span className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-black/[0.04] px-4 py-2 text-sm font-medium text-gray-500 dark:border-white/10 dark:bg-white/5 dark:text-white/50">
                    <svg viewBox="0 0 512 512" className="h-4 w-4" fill="currentColor">
                      <path d="M464 256A208 208 0 1 1 48 256a208 208 0 1 1 416 0zM0 256a256 256 0 1 0 512 0A256 256 0 1 0 0 256zM232 120V256c0 8 4 15.5 10.7 20l96 64c11 7.4 25.9 4.4 33.3-6.7s4.4-25.9-6.7-33.3L280 243.2V120c0-13.3-10.7-24-24-24s-24 10.7-24 24z" />
                    </svg>
                    {mobileApps.comingSoonPlayStore}
                  </span>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MobileApps;